import React, { FC } from 'react';
import styled from 'styled-components';
import IconButton from '~ComponentsImproved/IconButton';
import Icon from '~ComponentsImproved/Icon';
import Trophy from '~Assets/icons/trophy.svg';
import Modal from '~ComponentsImproved/Modal';
import { ModalContent } from '~Styles/modal';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import MultipleCompetitions from './Components/MultipleCompetitions';
import useModal from '~ComponentsImproved/Modal/useModal';
import useI18n from '~Translations';
import { useCompetition } from '~Providers/CompetitionsProvider';
import { handyBreakpoint } from '~Styles/breakpoints';
import { useSelector, useButton } from '~Hooks';
import { useCurrency } from '~Hooks';
import { useHistory } from 'react-router-dom';
import TopThree, { NotEnoughWrapper } from '~Scenes/Competitions/Components/TopThree';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { GetStaff } from './__queries__';
import {
  getCorrectValueBasedOnCompType,
  getStaffName,
} from '~Providers/CompetitionsProvider/utils';
import { hasSpecificSales } from '~Scenes/Competitions/utils';
import Loader from '~ComponentsImproved/Loader';

const Wrapper = styled(Box)`
  width: '100%';
`;

const ViewAllButtonWrapper = styled(Button)`
  min-height: 32px;
  padding: 12px;

  ${handyBreakpoint} {
    min-height: 24px;
    padding: 8px;
  }
`;

const PlacementWrapper = styled(Box)<{ isYou?: boolean }>`
  background-color: var(--colorBlueGray1000);
  border-radius: 8px;
  width: 100%;
  flex-grow: 1;
  border: ${({ isYou = false }) => (isYou ? '1px solid var(--colorBlueGray200)' : 'none')};
`;

const LeaderboardWrapper = styled(Box)`
  max-width: 496px;
  width: 100%;
  flex-grow: 1;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const STAFF_QUERY = gql`
  query GetStaff {
    user {
      id
      company {
        staffs {
          id
          name
        }
      }
    }
  }
`;

const CompetitionStatus: FC = () => {
  const { i18n } = useI18n();

  const staffQuery = useQuery<GetStaff>(STAFF_QUERY);
  const staff = staffQuery.data?.user.company.staffs ?? [];

  const venueId = useSelector((state) => state.venueId);
  const staffId = useSelector((state) => state.staff?.id);

  const modal = useModal();
  const currency = useCurrency();
  const history = useHistory();
  const competitions = useCompetition();

  const viewAllButton = useButton({
    onClick: () => history.push(`/venue/${venueId}/competitions`),
  });

  const isHandyBreakpoint = window.innerWidth <= 600;

  const renderStatus = (): JSX.Element => {
    if (staffQuery.loading) {
      return (
        <LoadingWrapper>
          <Loader />
        </LoadingWrapper>
      );
    }

    // This variable is used to check if the staff has a score in any of the competitions
    let hasScore = false;
    for (const competition of competitions.onGoing) {
      for (const score of competition.score) {
        if (score.staffId === staffId) {
          hasScore = true;
          break;
        }
      }
      if (hasScore) {
        break;
      }
    }

    if (!hasScore) {
      return (
        <Wrapper
          display="flex"
          flexDirection="column"
          gap="medium"
          justifyContent="center"
          alignItems="center">
          <NotEnoughWrapper>
            <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
              Your placement will be shown here once you've made a qualified sale
            </Paragraph>
          </NotEnoughWrapper>
        </Wrapper>
      );
    }

    switch (competitions.onGoing.length) {
      case 1:
        return (
          <Wrapper
            display="flex"
            flexDirection="column"
            gap="medium"
            justifyContent="center"
            alignItems="center">
            <TopThree
              isModalView
              competitionType={competitions.onGoing[0].type}
              hasOnlySpecificSales={hasSpecificSales(competitions.onGoing[0].onlySpecificSales)}
              leaderboard={competitions.onGoing[0].score}
              staffs={staff}
            />
            <LeaderboardWrapper display="flex" gap="small" flexDirection="column">
              {competitions.onGoing[0].score.slice(3).map((score, index) => (
                <PlacementWrapper
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  padding="medium"
                  key={score.staffId}
                  isYou={score.staffId === staffId}>
                  <Box display="flex" alignItems="center" gap="small">
                    <Paragraph level={isHandyBreakpoint ? 4 : 3} color="var(--colorBlueGray200)">
                      {index + 4}
                    </Paragraph>
                    <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
                      {score.staffId === staffId
                        ? i18n('Competitions.Status.You')
                        : getStaffName(score.staffId, staff, i18n)}
                    </Paragraph>
                  </Box>
                  <Paragraph level={isHandyBreakpoint ? 5 : 4} color="var(--colorBlueGray200)">
                    {getCorrectValueBasedOnCompType(
                      score,
                      competitions.onGoing[0].type,
                      hasSpecificSales(competitions.onGoing[0].onlySpecificSales),
                      currency.format,
                      i18n,
                    )}
                  </Paragraph>
                </PlacementWrapper>
              ))}
            </LeaderboardWrapper>
          </Wrapper>
        );
      default:
        return <MultipleCompetitions staff={staff ?? []} />;
    }
  };

  return (
    <div>
      <IconButton
        icon={<Icon src={Trophy} color="var(--colorBlueGray600)" />}
        text={i18n('Competitions.Score')}
        onClick={() => modal.show()}
      />
      <Modal isShowing={modal.isShowing} hide={() => modal.hide()}>
        <ModalContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            marginBottom="medium">
            <Heading level={2} color="#fff">
              {competitions.onGoing.length === 1
                ? competitions.onGoing[0].name
                : i18n('Competitions.Status.CurrentPlacements')}
            </Heading>

            <ViewAllButtonWrapper {...viewAllButton}>
              {i18n('Competitions.Status.ViewAll')}
            </ViewAllButtonWrapper>
          </Box>

          {renderStatus()}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default CompetitionStatus;
